const SCENE_ID = 'ghzc3zjn'
const PREFIX = '1l7ih0'

let captchaInstance: AliyunCaptchaInstance | null = null
let captchaInitialized = false
let initPromise: Promise<void> | null = null

let pendingSuccessCallback: ((param: string) => void) | null = null
let pendingCloseCallback: (() => void) | null = null

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.initAliyunCaptcha) {
      resolve()
      return
    }

    const existing = document.getElementById('aliyun-captcha-js')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Aliyun captcha script load error')))
      return
    }

    // 必须在 JS 加载前设置全局配置
    window.AliyunCaptchaConfig = {
      region: 'cn',
      prefix: PREFIX || ''
    }

    const script = document.createElement('script')
    script.id = 'aliyun-captcha-js'
    script.type = 'text/javascript'
    script.src = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Aliyun captcha script'))
    document.head.appendChild(script)
  })
}

function calcResponsiveStyle(): { slideWidth: number; rem: number } {
  const screenW = window.innerWidth
  if (screenW < 768) {
    // 移动端：目标宽度为屏幕 80%，slideStyle.width 最低 320px（阿里云限制）
    const slideWidth = 320
    const rem = Math.floor(((screenW * 0.8) / slideWidth) * 100) / 100
    return { slideWidth, rem }
  }
  // PC 端：固定 400px，不缩放
  return { slideWidth: 320, rem: 1 }
}

function injectCaptchaStyles(): void {
  if (document.getElementById('aliyun-captcha-styles')) return
  const style = document.createElement('style')
  style.id = 'aliyun-captcha-styles'
  style.textContent = `
    /* 移动端：容器居中、宽度 80% */
    @media (max-width: 767px) {
      #aliyun-captcha-container,
      #aliyun-captcha-container > * {
        max-width: 80vw !important;
        margin-left: auto !important;
        margin-right: auto !important;
      }
    }
    /* PC 端：容器居中、固定 440px */
    @media (min-width: 768px) {
      #aliyun-captcha-container,
      #aliyun-captcha-container > * {
        max-width: 440px !important;
        margin-left: auto !important;
        margin-right: auto !important;
      }
    }
  `
  document.head.appendChild(style)
}

function ensureCaptchaElements(): void {
  if (!document.getElementById('aliyun-captcha-container')) {
    const el = document.createElement('div')
    el.id = 'aliyun-captcha-container'
    document.body.appendChild(el)
  }
  if (!document.getElementById('aliyun-captcha-trigger')) {
    const btn = document.createElement('button')
    btn.id = 'aliyun-captcha-trigger'
    // 隐藏按钮，仅用于触发验证码，不展示给用户
    btn.style.cssText = 'position:fixed;opacity:0;pointer-events:none;width:0;height:0;overflow:hidden;'
    document.body.appendChild(btn)
  }
}

function scheduleReinit(): void {
  // 延迟重置，确保业务回调已执行完毕
  setTimeout(() => {
    captchaInitialized = false
    initPromise = null
    captchaInstance = null
    // 移除旧 DOM 元素，让下次 initAliyunCaptcha 重新绑定
    document.getElementById('aliyun-captcha-container')?.remove()
    document.getElementById('aliyun-captcha-trigger')?.remove()
    initAliyunCaptcha()
  }, 500)
}

async function doInit(): Promise<void> {
  ensureCaptchaElements()
  injectCaptchaStyles()
  await loadScript()

  const { slideWidth, rem } = calcResponsiveStyle()

  return new Promise((resolve, reject) => {
    if (!window.initAliyunCaptcha) {
      reject(new Error('initAliyunCaptcha is not available after script load'))
      return
    }
    try {
      window.initAliyunCaptcha({
        SceneId: SCENE_ID || '',
        mode: 'popup',
        element: '#aliyun-captcha-container',
        button: '#aliyun-captcha-trigger',
        success: (captchaVerifyParam: string) => {
          const cb = pendingSuccessCallback
          pendingSuccessCallback = null
          pendingCloseCallback = null
          cb?.(captchaVerifyParam)
          scheduleReinit()
        },
        fail: (result: unknown) => {
          const cb = pendingCloseCallback
          pendingSuccessCallback = null
          pendingCloseCallback = null
          cb?.()
        },
        getInstance: (instance: AliyunCaptchaInstance) => {
          captchaInstance = instance
          resolve()
        },
        onClose: () => {
          const cb = pendingCloseCallback
          pendingSuccessCallback = null
          pendingCloseCallback = null
          cb?.()
        },
        slideStyle: { width: slideWidth, height: 40 },
        rem,
        language: 'cn',
        zIndex: 9999999,
        onError: (errorInfo: { code: string; msg: string }) => {}
      })
    } catch (e) {
      reject(e)
    }
  })
}

/**
 * 初始化验证码（幂等，多次调用只执行一次）
 * 建议在组件 onMounted 时调用，让验证码提前加载资源（官方建议间隔 >2s）
 */
export async function initAliyunCaptcha(): Promise<void> {
  if (captchaInitialized) return
  if (initPromise) return initPromise

  initPromise = doInit()
    .then(() => {
      captchaInitialized = true
    })
    .catch((e) => {
      initPromise = null
    })

  return initPromise
}

/**
 * 触发验证码（点击隐藏按钮让验证码弹层/无痕验证启动）
 * @param onSuccess 验证通过回调，captchaVerifyParam 可传给后端验签
 * @param onClose   用户关闭弹窗或验证失败回调
 */
export function showAliyunCaptcha(onSuccess: (captchaVerifyParam: string) => void, onClose: () => void): boolean {
  if (!captchaInstance) {
    return false
  }
  const triggerBtn = document.getElementById('aliyun-captcha-trigger') as HTMLButtonElement | null
  if (!triggerBtn) {
    return false
  }
  pendingSuccessCallback = onSuccess
  pendingCloseCallback = onClose
  triggerBtn.click()
  return true
}

export function isAliyunCaptchaReady(): boolean {
  return captchaInstance !== null
}
